import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/utils/axiosInterceptor";
import type {
  StockTransactionCreateRequest,
  StockTransactionResponse,
  StockTransactionListResponse,
  StockTransactionFilters,
  StockHistoryItem,
  StockTransaction,
} from "@/types/stockTran";

const BASE_URL = "/stock-transactions";

// Create Stock In Transaction
export const useCreateStockIn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: StockTransactionCreateRequest) => {
      const response = await api.post<StockTransactionResponse>(
        `${BASE_URL}/in`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stockTransactions"] });
      queryClient.invalidateQueries({ queryKey: ["items"] });
    },
  });
};

// Create Stock Out Transaction
export const useCreateStockOut = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: StockTransactionCreateRequest) => {
      const response = await api.post<StockTransactionResponse>(
        `${BASE_URL}/out`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stockTransactions"] });
      queryClient.invalidateQueries({ queryKey: ["items"] });
    },
  });
};

// Get All Stock Transactions
export const useStockTransactions = (filters?: StockTransactionFilters) => {
  return useQuery({
    queryKey: ["stockTransactions", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.voucherType) params.append("type", filters.voucherType);
      if (filters?.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters?.dateTo) params.append("dateTo", filters.dateTo);
      if (filters?.search) params.append("search", filters.search);
      if (filters?.page) params.append("page", filters.page.toString());
      if (filters?.limit) params.append("limit", filters.limit.toString());

      const response = await api.get<StockTransactionListResponse>(
        `${BASE_URL}?${params.toString()}`
      );
      return response.data;
    },
  });
};

// Get Single Stock Transaction
export const useStockTransaction = (id: string | undefined) => {
  return useQuery({
    queryKey: ["stockTransaction", id],
    queryFn: async () => {
      if (!id) throw new Error("Transaction ID is required");
      const response = await api.get<StockTransactionResponse>(
        `${BASE_URL}/${id}`
      );
      return response.data;
    },
    enabled: !!id,
  });
};

// Get Voucher Details by Voucher Number
export const useVoucherDetails = (voucherNumber: string | undefined) => {
  return useQuery({
    queryKey: ["voucherDetails", voucherNumber],
    queryFn: async () => {
      if (!voucherNumber) throw new Error("Voucher number is required");
      const response = await api.get<{ data: StockTransaction[] }>(
        `${BASE_URL}/voucher/${voucherNumber}`
      );
      return response.data;
    },
    enabled: !!voucherNumber,
  });
};

// Update Stock Transaction
export const useUpdateStockTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: StockTransactionCreateRequest;
    }) => {
      const response = await api.put<StockTransactionResponse>(
        `${BASE_URL}/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stockTransactions"] });
      queryClient.invalidateQueries({ queryKey: ["stockTransaction"] });
      queryClient.invalidateQueries({ queryKey: ["items"] });
    },
  });
};

// Delete Stock Transaction by Voucher Number
export const useDeleteStockTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (voucherNumber: string) => {
      const response = await api.delete(`${BASE_URL}/voucher/${voucherNumber}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stockTransactions"] });
      queryClient.invalidateQueries({ queryKey: ["voucherDetails"] });
      queryClient.invalidateQueries({ queryKey: ["items"] });
    },
  });
};

// Edit Stock In Transaction
export const useEditStockIn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      voucherNumber,
      data,
    }: {
      voucherNumber: string;
      data: StockTransactionCreateRequest;
    }) => {
      const response = await api.put<StockTransactionResponse>(
        `${BASE_URL}/in/${voucherNumber}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stockTransactions"] });
      queryClient.invalidateQueries({ queryKey: ["voucherDetails"] });
      queryClient.invalidateQueries({ queryKey: ["items"] });
    },
  });
};

// Edit Stock Out Transaction
export const useEditStockOut = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      voucherNumber,
      data,
    }: {
      voucherNumber: string;
      data: StockTransactionCreateRequest;
    }) => {
      const response = await api.put<StockTransactionResponse>(
        `${BASE_URL}/out/${voucherNumber}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stockTransactions"] });
      queryClient.invalidateQueries({ queryKey: ["voucherDetails"] });
      queryClient.invalidateQueries({ queryKey: ["items"] });
    },
  });
};

// Get Stock History for an Item
export const useStockHistory = (
  itemId: string | undefined,
  filters?: { dateFrom?: string; dateTo?: string }
) => {
  return useQuery({
    queryKey: ["stockHistory", itemId, filters],
    queryFn: async () => {
      if (!itemId) throw new Error("Item ID is required");

      const params = new URLSearchParams();
      if (filters?.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters?.dateTo) params.append("dateTo", filters.dateTo);

      const response = await api.get<{ data: StockHistoryItem[] }>(
        `${BASE_URL}/history/${itemId}?${params.toString()}`
      );
      return response.data;
    },
    enabled: !!itemId,
  });
};

// Hook to use all stock transaction operations
export const useStockTransactionOperations = () => {
  const createStockIn = useCreateStockIn();
  const createStockOut = useCreateStockOut();
  const updateTransaction = useUpdateStockTransaction();
  const deleteTransaction = useDeleteStockTransaction();

  return {
    createStockIn,
    createStockOut,
    updateTransaction,
    deleteTransaction,
    isLoading:
      createStockIn.isPending ||
      createStockOut.isPending ||
      updateTransaction.isPending ||
      deleteTransaction.isPending,
  };
};

// Stock Adjustment - Mode 1: Relative Adjustment
export const useCreateRelativeAdjustment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      reference?: string | null;
      entryDate?: string;
      reason: string;
      prefixId?: string;
      items: Array<{
        stockCode: string;
        adjustmentQty: number;
        unitCost?: number;
      }>;
    }) => {
      const response = await api.post<{
        message: string;
        data: {
          adjustmentType: string;
          results: any[];
          varianceReport: any[];
        };
      }>(`${BASE_URL}/adjustment/relative`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stockTransactions"] });
      queryClient.invalidateQueries({ queryKey: ["items"] });
    },
  });
};

// Stock Adjustment - Mode 2: Physical Count
export const useCreatePhysicalCount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      reference?: string | null;
      entryDate?: string;
      reason: string;
      prefixId?: string;
      items: Array<{
        stockCode: string;
        countedQty: number;
        newWeightedAverage?: number;
      }>;
    }) => {
      const response = await api.post<{
        message: string;
        data: {
          adjustmentType: string;
          results: any[];
          varianceReport: any[];
        };
      }>(`${BASE_URL}/adjustment/physical-count`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stockTransactions"] });
      queryClient.invalidateQueries({ queryKey: ["items"] });
    },
  });
};
